import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { FeatureFlagIdRequestSchemaSecrets } from 'c1gen/features'
import { C1Alert, C1IconButton } from 'c1ui'
import { C1Button } from 'c1ui/components/C1Button'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress'
import { C1LoadingButton } from 'components/core/button'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { MultiAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormCheckbox } from 'components/core/RHKFormComponents/C1FormCheckbox'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { C1LabeledChipsInput } from 'components/core/RHKFormComponents/C1LabeledChipsInput'
import { C1LabeledSelect } from 'components/core/RHKFormComponents/C1LabeledSelect'
import { C1LabeledTextField } from 'components/core/RHKFormComponents/C1LabeledTextField'
import { C1ToggleButtons } from 'components/core/RHKFormComponents/C1ToggleButtons'
import { useFeatureChecker } from 'components/no-ui/capability'
import { useAlert } from 'hooks/useAlert'
import { useSimpleModal } from 'lib/modals'
import { PlusCircle, Trash2 } from 'lucide-react'
import { Form } from 'pbts/c1/api/form/v1/form'
import { JustificationVisibility, RequestSchema, RequestSchemaServiceCountBindingsForSchemaRequest, RequestSchemaServiceCreateRequest, RequestSchemaServiceDeleteRequest, RequestSchemaServiceGetRequest, RequestSchemaServiceUpdateRequest } from 'pbts/c1/api/request_schema/v1/request_schema'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import { FC, useCallback, useEffect } from 'react'
import { Control, FieldArrayWithId, FieldErrors, useFieldArray, UseFieldArrayRemove, useForm, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { getRequestSchemaApi, countBindingsForSchemaApi, createRequestSchemaApi, updateRequestSchemaApi, deleteRequestSchemaApi } from 'redux_toolkit/request-schema'
import * as yup from 'yup'
import { ConfirmDeleteSchemaDialog } from './ConfirmDeleteSchemaDialog'
import { formFieldToProto, protoToFormField } from './fieldConversion'

export enum RequestSchemaMode {
    CreateNew = 'create-new',
    ModifyExisting = 'modify-existing',
}

export enum FieldTypeOption {
    Text = 'text',
    Password = 'password',
    Checkbox = 'checkbox',
    MultiStringChips = 'multi_string_chips',
    TextArea = 'text_area',
    Select = 'select',
}

const getFieldTypeLabels = (intl: IntlShape): Record<FieldTypeOption, string> => ({
    [FieldTypeOption.Text]: intl.formatMessage({
        id: 'field_type_text',
        defaultMessage: 'Text',
    }),
    [FieldTypeOption.Password]: intl.formatMessage({
        id: 'field_type_password',
        defaultMessage: 'Password',
    }),
    [FieldTypeOption.Checkbox]: intl.formatMessage({
        id: 'field_type_checkbox',
        defaultMessage: 'Checkbox',
    }),
    [FieldTypeOption.MultiStringChips]: intl.formatMessage({
        id: 'field_type_multi_string_chips',
        defaultMessage: 'Multi string chips',
    }),
    [FieldTypeOption.TextArea]: intl.formatMessage({
        id: 'field_type_text_area',
        defaultMessage: 'Text area',
    }),
    [FieldTypeOption.Select]: intl.formatMessage({
        id: 'field_type_dropdown',
        defaultMessage: 'Drop down',
    }),
})

export const getSchemaNameRequiredError = (intl: IntlShape): string => intl.formatMessage({
    id: 'request_schema_name_required',
    defaultMessage: 'Schema name is required',
})

export const getFieldLabelRequiredError = (intl: IntlShape): string => intl.formatMessage({
    id: 'request_schema_field_label_required',
    defaultMessage: 'Field label is required',
})

export const getFieldLabelAlphanumericError = (intl: IntlShape): string => intl.formatMessage({
    id: 'request_schema_field_label_alphanumeric',
    defaultMessage: 'Display name must contain at least one letter or number',
})

export const getSelectOptionsRequiredError = (intl: IntlShape): string => intl.formatMessage({
    id: 'request_schema_select_options_required',
    defaultMessage: 'At least one option is required',
})

const getFieldSchema = (intl: IntlShape): yup.ObjectSchema<any> => yup.object({ // eslint-disable-line @typescript-eslint/no-explicit-any
    field: yup.object({
        type: yup.string().oneOf(Object.values(FieldTypeOption)).required(),
        display_name: yup.string()
            .required(getFieldLabelRequiredError(intl))
            .test('has-alphanumeric', getFieldLabelAlphanumericError(intl), (value): boolean => {
                if (!value) return true
                return /[a-zA-Z0-9]/.test(value)
            }), // label to users
        description: yup.string().optional(), // helper text to users
        name: yup.string().optional(), // id of the field, not passed to the backend to be set
        text: yup.object({
            default_value: yup.string().optional(),
            placeholder: yup.string().optional(),
            required: yup.boolean().default(false),
        }),
        password: yup.object({
            default_value: yup.string().optional(),
            placeholder: yup.string().optional(),
            required: yup.boolean().default(false),
        }),
        multi_string_chips: yup.object({
            default_values: yup.array().of(yup.string()).optional(),
            placeholder: yup.string().optional(),
            required: yup.boolean().default(false),
        }),
        checkbox: yup.object({
            default_value: yup.boolean().default(false),
        }),
        text_area: yup.object({
            default_value: yup.string().optional(),
            placeholder: yup.string().optional(),
            required: yup.boolean().default(false),
        }),
        select: yup.object({
            options: yup.array().of(yup.string()).optional(),
            default_value: yup.string().optional(),
            placeholder: yup.string().optional(),
            required: yup.boolean().default(false),
        }).when('type', {
            is: FieldTypeOption.Select,
            then: (schema) => schema.shape({
                options: yup.array().of(yup.string()).min(1, getSelectOptionsRequiredError(intl))
                    .required(getSelectOptionsRequiredError(intl)),
            }),
        }),
    }),
})

export const getSchema = (intl: IntlShape): yup.ObjectSchema<any> => yup.object({ // eslint-disable-line @typescript-eslint/no-explicit-any
    fields: yup.array().of(getFieldSchema(intl)).min(1),
    mode: yup.string().oneOf(Object.values(RequestSchemaMode)).default(RequestSchemaMode.CreateNew),
    name: yup.string().required(getSchemaNameRequiredError(intl)).min(1),
    description: yup.string().optional(),
    hideJustification: yup.boolean().default(false),
})

export const getFormFieldsSchema = (intl: IntlShape): yup.ObjectSchema<any> => yup.object({ // eslint-disable-line @typescript-eslint/no-explicit-any
    name: yup.string().required(getSchemaNameRequiredError(intl)).min(1),
    description: yup.string().optional(),
    fields: yup.array().of(getFieldSchema(intl)).min(1, 'At least one field is required').required(),
})

type RequestSchemaDrawerProps = {
    mode: RequestSchemaMode
    closeDrawer: () => void
    schemaIdToModify?: string
    handleSchemaIdChange: (schemaId: string | undefined) => void
    assignedSchemaId?: string
    setCanClose?: (canClose: boolean) => void
}

export const defaultSchemaStep = {
    field: {
        display_name: '',
        type: FieldTypeOption.Text,
        description: '',
        required: false,
        name: '',
        text: {
            default_value: '',
            placeholder: '',
            required: false,
        },
        password: {
            default_value: '',
            placeholder: '',
            required: false,
        },
        checkbox: {
            default_value: false,
        },
        multi_string_chips: {
            default_values: [],
            placeholder: '',
            required: false,
        },
        text_area: {
            default_value: '',
            placeholder: '',
            required: false,
        },
        select: {
            options: [],
            default_value: '',
            placeholder: '',
            required: false,
        },
    },
}

export type RequestSchemaFormFieldValues = yup.InferType<ReturnType<typeof getSchema>>

export const RequestSchemaDrawer: FC<RequestSchemaDrawerProps> =
    ({ closeDrawer, mode, schemaIdToModify, handleSchemaIdChange, assignedSchemaId, setCanClose }) => {
        const intl = useIntl()
        const schema = getSchema(intl)
        const toast = useAlert()
        const { open } = useSimpleModal()

        const updateSchema = updateRequestSchemaApi()
        const createSchema = createRequestSchemaApi()
        const deleteSchema = deleteRequestSchemaApi()

        const { data: schemaToModifyData, isLoading: isLoadingSchemaToModify, error: schemaToModifyError } = getRequestSchemaApi.useGet(
            RequestSchemaServiceGetRequest.create({ requestSchemaId: schemaIdToModify }),
            {
                actionable: !!schemaIdToModify,
            },
        )

        const {
            register, control, formState: { errors, isDirty }, watch, handleSubmit, setValue,
        } = useForm({
            resolver: yupResolver(schema),
            mode: 'all',
            defaultValues: schema.cast({ fields: [defaultSchemaStep], name: '', description: '', hideJustification: false }),
        })
        const { fields, append, remove } = useFieldArray({ control, name: 'fields' })

        useEffect(() => {
            if (schemaToModifyData) {
                setValue('name', schemaToModifyData?.requestSchema?.form?.displayName)
                setValue('description', schemaToModifyData?.requestSchema?.form?.description)
                setValue('fields', schemaToModifyData?.requestSchema?.form?.fields?.map((field) => protoToFormField(field)))
                setValue('hideJustification', schemaToModifyData?.requestSchema?.justificationVisibility === JustificationVisibility.HIDE)
            }
        }, [schemaToModifyData, setValue])

        useEffect(() => {
            if (setCanClose) {
                setCanClose(!isDirty)
            }
        }, [isDirty, setCanClose])

        const onSubmit = useCallback(async (fieldValues: RequestSchemaFormFieldValues): Promise<void> => {
            try {
                if (mode === RequestSchemaMode.CreateNew) {
                    const { data: newSchema } = await createSchema.mutate(RequestSchemaServiceCreateRequest.create({
                        name: fieldValues.name,
                        description: fieldValues.description,
                        fields: fieldValues.fields.map((field) => formFieldToProto(field)),
                    }), { notify: true })
                    newSchema?.requestSchema?.id && handleSchemaIdChange(newSchema?.requestSchema?.id)
                    if (setCanClose) {
                        setCanClose(true)
                    }
                    closeDrawer()
                } else if (mode === RequestSchemaMode.ModifyExisting) {
                    await updateSchema.mutate(RequestSchemaServiceUpdateRequest.create({
                        requestSchema: RequestSchema.create({
                            id: schemaIdToModify,
                            form: Form.create({
                                displayName: fieldValues.name,
                                description: fieldValues.description,
                                fields: fieldValues.fields.map((field) => formFieldToProto(field)),
                            }),
                            justificationVisibility: fieldValues.hideJustification
                                ? JustificationVisibility.HIDE
                                : JustificationVisibility.SHOW,
                        }),
                        updateMask: FieldMask.create({ paths: ['form', 'justification_visibility'] }),
                    }), { notify: true })
                    if (setCanClose) {
                        setCanClose(true)
                    }
                    closeDrawer()
                }
            } catch (error) {
                if (error instanceof RpcError) {
                    toast(<FormattedMessage
                        id="error_modifying_request_schema"
                        defaultMessage="Error modifying request schema: {error}"
                        values={{ error: error.message }}
                    />, 'error', 0)
                }
                console.error('Failed to update request schema and bindings:', error)
            }
        }, [createSchema, updateSchema, closeDrawer, toast, schemaIdToModify, mode, handleSchemaIdChange, setCanClose])

        const handleDeleteForm = useCallback(async (): Promise<void> => {
            if (schemaIdToModify) {
                try {
                    await deleteSchema.mutate(RequestSchemaServiceDeleteRequest
                        .create({ requestSchemaId: schemaIdToModify }), { notify: true })
                    // if the one currently being deleted is the on assigned, set to dropdown to none
                    // otherwise set the dropdown back to the assigned schema
                    handleSchemaIdChange(schemaIdToModify === assignedSchemaId ? undefined : assignedSchemaId)
                    closeDrawer()
                } catch (error) {
                    if (error instanceof RpcError) {
                        toast(<FormattedMessage
                            id="error_deleting_request_schema"
                            defaultMessage="Error deleting request schema: {error}"
                            values={{ error: error.message }}
                        />, 'error', 0)
                    }
                    console.error('Failed to delete request schema:', error)
                }
            }
        }, [schemaIdToModify, handleSchemaIdChange, closeDrawer, toast, assignedSchemaId, deleteSchema])

        const { data: bindingsCount, error: bindingsCountError } = countBindingsForSchemaApi.useCountBindingsForSchema(
            RequestSchemaServiceCountBindingsForSchemaRequest.create({ requestSchemaId: schemaIdToModify }),
            {
                actionable: !!schemaIdToModify,
            },
        )

        const openDeleteConfirmation = useCallback(async (): Promise<void> => {
            open(ConfirmDeleteSchemaDialog, {
                onConfirmDelete: handleDeleteForm,
                schemaName: schemaToModifyData?.requestSchema?.form?.displayName ?? '',
                numBindings: bindingsCount?.numBindings ?? 0,
            })()
        }, [open, handleDeleteForm, schemaToModifyData, bindingsCount])

        const sanitizeName = (name: string | undefined): string => {
            if (!name) return ''
            return name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '')
        }

        const handleFieldDisplayNameChange = useCallback(() => {
            const fieldDisplayNameMapToIds = fields.reduce((acc, field, idx) => {
                const displayName = watch(`fields.${idx}.field.display_name`)
                const sanitizedName = sanitizeName(displayName)
                acc[sanitizedName] = acc[sanitizedName] ? [...acc[sanitizedName], field.id] : [field.id]
                return acc
            }, {} as Record<string, string[]>)
            fields.forEach((field, idx) => {
                const displayName = watch(`fields.${idx}.field.display_name`)
                const sanitizedName = sanitizeName(displayName)
                const idIdx = fieldDisplayNameMapToIds[sanitizedName].indexOf(field.id)
                setValue(`fields.${idx}.field.name`, `${sanitizedName}_${idIdx}`)
            })
        }, [fields, setValue, watch])

        return <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
            <C1DrawerInner>
                <C1DrawerInner.Header>
                    <Stack spacing={2}>
                        <Typography variant="h3">
                            {mode === RequestSchemaMode.CreateNew ? <FormattedMessage id="new_schema_title" defaultMessage="Create new form fields" />
                                : <FormattedMessage id="edit_form_fields_title" defaultMessage="Edit form fields" />}
                        </Typography>
                        <Typography color="text.secondary">
                            <FormattedMessage id="components_entitlement_request_schema_drawer_description" defaultMessage="Fields created here will be appended to the request form" />
                        </Typography>
                    </Stack>
                </C1DrawerInner.Header>
                <C1DrawerInner.Content>
                    <MultiAPIError errs={[schemaToModifyError, bindingsCountError]} />
                    <Stack spacing={3}>
                        <Stack spacing={2} py={2}>
                            <C1LabeledTextField
                                register={register}
                                name="name"
                                label={<FormattedMessage id="request_schema_name" defaultMessage="Form name" />}
                                error={!!errors.name}
                                helperText={errors?.name?.message as string}
                            />
                            <C1LabeledTextField
                                register={register}
                                name="description"
                                label={<Stack direction="row" spacing={1} alignItems="center">
                                    <C1FormLabel label={<FormattedMessage id="request_schema_description" defaultMessage="Description" />} />
                                    <Typography variant="caption" color="text.secondary">
                                        <FormattedMessage id="description_optional" defaultMessage="(optional)" />
                                    </Typography>
                                </Stack>}
                                error={!!errors.description}
                            />
                            <C1FormCheckbox
                                control={control}
                                name="hideJustification"
                                label={<FormattedMessage id="request_schema_hide_justification" defaultMessage="Hide justification field" />}
                            />
                        </Stack>
                        {isLoadingSchemaToModify && mode === RequestSchemaMode.ModifyExisting && <C1CircularProgress />}
                        {(mode === RequestSchemaMode.CreateNew || (mode === RequestSchemaMode.ModifyExisting && schemaToModifyData)) && (
                            <>
                                <Stack spacing={5}>
                                    {fields.map((field, index) => {
                                        return <RequestSchemaField
                                            control={control}
                                            errors={errors}
                                            index={index}
                                            key={field.id}
                                            register={register}
                                            watch={watch}
                                            remove={remove}
                                            fields={fields}
                                            handleFieldDisplayNameChange={handleFieldDisplayNameChange}
                                            setValue={setValue}
                                        />
                                    })}
                                </Stack>
                                <C1Button
                                    onClick={(): void => append(defaultSchemaStep)}
                                    startIcon={<C1Icon icon={PlusCircle} />}
                                    aria-label={intl.formatMessage({
                                        id: 'request_schema_field_add',
                                        defaultMessage: 'Add field',
                                    })}
                                >
                                    <FormattedMessage id="request_schema_field_add" defaultMessage="Add field" />
                                </C1Button>
                            </>
                        )}
                    </Stack>
                </C1DrawerInner.Content>
                <C1DrawerInner.Footer>
                    <Stack spacing={2} flex={1}>
                        {mode === RequestSchemaMode.ModifyExisting && schemaToModifyData && bindingsCount && bindingsCount.numBindings > 1
                            && (
                                <C1Alert variant="standard" severity="warning">
                                    <Typography>
                                        <FormattedMessage
                                            defaultMessage="{count, plural, =0 {The changes will be applied to the other 0 entitlements associated with this schema.} one {The changes will be applied to the other # entitlement associated with this schema.} other {The changes will be applied to the other # entitlements associated with this schema.}}"
                                            values={{ count: (bindingsCount?.numBindings ?? 0) - 1 }}
                                            id="components_entitlement_request_schema_drawer_save_info"
                                        />
                                    </Typography>
                                </C1Alert>
                            )}
                        <Stack direction="row" spacing={2} justifyContent={mode === RequestSchemaMode.ModifyExisting ? 'space-between' : 'flex-end'}>
                            {mode === RequestSchemaMode.ModifyExisting && <C1Button
                                variant="outlined"
                                color="error"
                                onClick={openDeleteConfirmation}
                                aria-label={intl.formatMessage({
                                    id: 'request_schema_delete_form',
                                    defaultMessage: 'Delete form',
                                })}
                            >
                                <FormattedMessage id="request_schema_delete_form" defaultMessage="Delete form" />
                            </C1Button>}
                            <Stack direction="row" spacing={2}>
                                <C1Button
                                    variant="outlined"
                                    onClick={closeDrawer}
                                    aria-label={intl.formatMessage({
                                        id: 'request_schema_cancel',
                                        defaultMessage: 'Cancel',
                                    })}
                                >
                                    <FormattedMessage id="request_schema_cancel" defaultMessage="Cancel" />
                                </C1Button>
                                <C1LoadingButton
                                    aria-label={intl.formatMessage({
                                        id: 'request_schema_save',
                                        defaultMessage: 'Save',
                                    })}
                                    type="submit"
                                    variant="contained"
                                    loading={createSchema.inProgress || updateSchema.inProgress}
                                >
                                    <FormattedMessage id="request_schema_save" defaultMessage="Save" />
                                </C1LoadingButton>
                            </Stack>
                        </Stack>
                    </Stack>
                </C1DrawerInner.Footer>
            </C1DrawerInner>
        </form>
    }

type RequestSchemaFieldProps = {
    control: Control<RequestSchemaFormFieldValues>
    errors: FieldErrors<RequestSchemaFormFieldValues>
    index: number
    register: UseFormRegister<RequestSchemaFormFieldValues>
    watch: UseFormWatch<RequestSchemaFormFieldValues>
    remove: UseFieldArrayRemove
    fields: FieldArrayWithId<RequestSchemaFormFieldValues, 'fields', 'id'>[]
    handleFieldDisplayNameChange: () => void
    setValue: UseFormSetValue<RequestSchemaFormFieldValues>
}

export const RequestSchemaField: FC<RequestSchemaFieldProps> = ({ control, errors, index, register, watch, remove, fields,
    handleFieldDisplayNameChange, setValue }) => {
    const fieldType = watch(`fields.${index}.field.type`)
    const intl = useIntl()
    const hasRequestSchemaSecretsFeature = useFeatureChecker(FeatureFlagIdRequestSchemaSecrets)
    const fieldDisplayName = watch(`fields.${index}.field.display_name`)
    const options = fieldType === FieldTypeOption.Select ? watch(`fields.${index}.field.select.options`) || [] : [] // eslint-disable-line react-hooks/exhaustive-deps
    const defaultValue = fieldType === FieldTypeOption.Select ? watch(`fields.${index}.field.select.default_value`) : undefined

    useEffect(() => {
        handleFieldDisplayNameChange()
    }, [fieldDisplayName, handleFieldDisplayNameChange])

    // For select fields, if the default value is set, and then options are changed to not
    // include the default value, reset the default value to empty
    useEffect(() => {
        if (fieldType === FieldTypeOption.Select && defaultValue && !options.includes(defaultValue)) {
            setValue(`fields.${index}.field.select.default_value`, '')
        }
    }, [fieldType, index, setValue, options, defaultValue])

    return <Stack spacing={2} py={1}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" width="100%">
            <Stack direction="row" spacing={2} alignItems="flex-end">
                <Typography variant="h5">
                    <FormattedMessage id="request_schema_field_type" defaultMessage="Field {index}" values={{ index: index + 1 }} />
                </Typography>
                {fieldDisplayName && fieldDisplayName.length > 0 && <Typography variant="caption" color="text.secondary">
                    {`Id: ${watch(`fields.${index}.field.name`)}`}
                </Typography>}
            </Stack>
            {(index > 0 || fields.length > 1) && <C1IconButton
                onClick={(): void => remove(index)}
                aria-label={intl.formatMessage({
                    id: 'request_schema_field_delete',
                    defaultMessage: 'Delete field',
                })}
            >
                <C1Icon icon={Trash2} />
            </C1IconButton>
            }
        </Stack>
        <C1LabeledSelect
            control={control}
            name={`fields.${index}.field.type`}
            error={!!errors.fields?.[index]?.field?.type}
            options={Object.values(FieldTypeOption)
                .filter((type) => type !== FieldTypeOption.MultiStringChips) // disabled for now due to confusion from customers about this field
                .filter((type) => type !== FieldTypeOption.Password || hasRequestSchemaSecretsFeature)
                .map((type) => ({
                    label: getFieldTypeLabels(intl)[type],
                    value: type,
                    id: type,
                }))}
            label={<FormattedMessage id="request_schema_data_type" defaultMessage="Data type" />}
        />
        <C1LabeledTextField
            register={register}
            name={`fields.${index}.field.display_name`}
            error={!!errors.fields?.[index]?.field?.display_name}
            label={<FormattedMessage id="request_schema_field_label" defaultMessage="Field label" />}
            helperText={errors.fields?.[index]?.field?.display_name?.message}
        />
        {fieldType === FieldTypeOption.Select && (
            <>
                <C1LabeledChipsInput
                    control={control}
                    name={`fields.${index}.field.select.options`}
                    error={!!errors.fields?.[index]?.field?.select?.options}
                    label={<Stack direction="row" spacing={1} alignItems="center">
                        <C1FormLabel label={<FormattedMessage id="select_options_label" defaultMessage="Options" />} />
                    </Stack>}
                    helperText={errors.fields?.[index]?.field?.select?.options?.message}
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Hit enter to add an option',
                        id: 'component_labeled_chips_input_placeholder_options',
                    })}
                />
            </>
        )}
        <C1LabeledTextField
            register={register}
            name={`fields.${index}.field.description`}
            error={!!errors.fields?.[index]?.field?.description}
            label={<Stack direction="row" spacing={1} alignItems="center">
                <C1FormLabel label={<FormattedMessage id="request_schema_field_helper_text_label" defaultMessage="Helper text" />} />
                <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="helper_text_optional" defaultMessage="(optional)" />
                </Typography>
            </Stack>}
        />
        {fieldType === FieldTypeOption.Checkbox && (
            <Stack spacing={0}>
                <C1FormLabel label={<FormattedMessage id="is_checked_by_default" defaultMessage="Is checked by default" />} />
                <C1ToggleButtons
                    control={control}
                    name={`fields.${index}.field.checkbox.default_value`}
                    options={[
                        {
                            children: <FormattedMessage id="checkbox_default_value_true" defaultMessage="true" />,
                            value: true,
                        },
                        {
                            children: <FormattedMessage id="checkbox_default_value_false" defaultMessage="false" />,
                            value: false,
                        },
                    ]}
                />
            </Stack>
        )}
        {fieldType === FieldTypeOption.MultiStringChips && (
            <>
                <C1LabeledChipsInput
                    control={control}
                    name={`fields.${index}.field.multi_string_chips.default_values`}
                    error={!!errors.fields?.[index]?.field?.multi_string_chips?.default_values}
                    label={<Stack direction="row" spacing={1} alignItems="center">
                        <C1FormLabel label={<FormattedMessage id="multi_string_chips_default_values_label" defaultMessage="Default values" />} />
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage id="default_values_optional" defaultMessage="(optional)" />
                        </Typography>
                    </Stack>}
                    helperText={errors.fields?.[index]?.field?.multi_string_chips?.default_values?.message}
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Hit enter to add a value',
                        id: 'component_labeled_chips_input_placeholder',
                    })}
                />
            </>
        )}
        {(fieldType === FieldTypeOption.Text || fieldType === FieldTypeOption.Password || fieldType === FieldTypeOption.TextArea)
            && (
                <>
                    <C1LabeledTextField
                        register={register}
                        name={`fields.${index}.field.${fieldType}.default_value`}
                        error={!!errors.fields?.[index]?.field?.[fieldType]?.default_value}
                        label={<Stack direction="row" spacing={1} alignItems="center">
                            <C1FormLabel label={<FormattedMessage id="request_schema_field_default_value_label" defaultMessage="Default value" />} />
                            <Typography variant="caption" color="text.secondary">
                                <FormattedMessage id="default_value_optional" defaultMessage="(optional)" />
                            </Typography>
                        </Stack>}
                        helperText={errors.fields?.[index]?.field?.[fieldType]?.default_value?.message}
                    />
                </>
            )}
        {fieldType === FieldTypeOption.Select && (
            <>
                <C1LabeledSelect
                    control={control}
                    name={`fields.${index}.field.select.default_value`}
                    error={!!errors.fields?.[index]?.field?.select?.default_value}
                    label={<FormattedMessage id="request_schema_field_default_value_label_select" defaultMessage="Default value" />}
                    options={[
                        {
                            id: 'none',
                            value: '',
                            label: <Typography component="span" variant="disabled" color="text.disabled">
                                <FormattedMessage
                                    defaultMessage="None"
                                    id="request_schema_field_default_value_label_select_none"
                                />
                            </Typography>,
                        },
                        ...(watch(`fields.${index}.field.select.options`) || []).map((option: string, optionIndex: number) => ({
                            id: `${option}_${optionIndex}`,
                            value: option,
                            label: option,
                        })),
                    ]}
                    displayEmpty
                />
            </>
        )}
        {(fieldType === FieldTypeOption.MultiStringChips || fieldType === FieldTypeOption.Text
            || fieldType === FieldTypeOption.Password || fieldType === FieldTypeOption.TextArea ||
            fieldType === FieldTypeOption.Select) &&
            (<>
                <C1LabeledTextField
                    register={register}
                    name={`fields.${index}.field.${fieldType}.placeholder`}
                    error={!!errors.fields?.[index]?.field?.[fieldType]?.placeholder}
                    label={<Stack direction="row" spacing={1} alignItems="center">
                        <C1FormLabel label={<FormattedMessage id="request_schema_field_placeholder_label" defaultMessage="Placeholder" />} />
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage id="placeholder_optional" defaultMessage="(optional)" />
                        </Typography>
                    </Stack>}
                    helperText={errors.fields?.[index]?.field?.[fieldType]?.placeholder?.message}
                />
                <C1FormCheckbox
                    control={control}
                    name={`fields.${index}.field.${fieldType}.required`}
                    error={!!errors.fields?.[index]?.field?.[fieldType]?.required}
                    label={<FormattedMessage id="request_schema_field_required" defaultMessage="Required" />}
                />
            </>
            )}
    </Stack>
}
